"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { Time } from "@/helpers/time";
import { calculateTimeLeft } from "@/helpers/waves/time.utils";
import type { ApprovalWaveCloseStatus } from "@/helpers/waves/approve-wave.helpers";
import { getApprovalWindowEndTime } from "@/helpers/waves/approve-wave.helpers";
interface WaveApprovalStatusBarProps {
  readonly approvedCount: number | null;
  readonly closeStatus: ApprovalWaveCloseStatus;
  readonly isApprovalStatusError?: boolean | undefined;
  readonly retryApprovalStatus?: (() => void) | null | undefined;
  readonly wave: ApiWave;
}
const formatTimeLeft = (targetTime: number, currentMillis: number): string => {
  const timeLeft = calculateTimeLeft(targetTime, currentMillis);
  if (timeLeft.days > 0) {
    return `${timeLeft.days}d ${timeLeft.hours}h left`;
  }
  if (timeLeft.hours > 0) {
    return `${timeLeft.hours}h ${timeLeft.minutes}m left`;
  }
  return `${timeLeft.minutes}m left`;
};
const getCurrentMillisForStatusRender = (_clockTick: number, _endTime: number | null, _closeStatus: ApprovalWaveCloseStatus): number => Time.currentMillis();
export default function WaveApprovalStatusBar(t0) {
  const $ = _c(41);
  const {
    approvedCount,
    closeStatus,
    isApprovalStatusError: t1,
    retryApprovalStatus: t2,
    wave
  } = t0;
  const isApprovalStatusError = t1 === undefined ? false : t1;
  const retryApprovalStatus = t2 === undefined ? null : t2;
  const winningThreshold = wave.wave.winning_threshold;
  const maxWinners = wave.wave.max_winners;
  let t3;
  if ($[0] !== wave) {
    t3 = getApprovalWindowEndTime(wave);
    $[0] = wave;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const endTime = t3;
  const [clockTick, setClockTick] = useState(0);
  let t4;
  if ($[2] !== clockTick || $[3] !== closeStatus || $[4] !== endTime) {
    t4 = getCurrentMillisForStatusRender(clockTick, endTime, closeStatus);
    $[2] = clockTick;
    $[3] = closeStatus;
    $[4] = endTime;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const currentMillis = t4;
  let t5;
  let t6;
  if ($[6] !== closeStatus || $[7] !== endTime) {
    t5 = () => {
      if (closeStatus !== null || endTime === null) {
        return;
      }
      const intervalId = globalThis.setInterval(() => {
        setClockTick(_temp);
      }, 1000);
      return () => {
        globalThis.clearInterval(intervalId);
      };
    };
    t6 = [closeStatus, endTime];
    $[6] = closeStatus;
    $[7] = endTime;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== winningThreshold) {
    t7 = typeof winningThreshold === "number" && Number.isFinite(winningThreshold) ? formatNumberWithCommas(winningThreshold) : "Not set";
    $[10] = winningThreshold;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const thresholdLabel = t7;
  let t8;
  bb0: {
    if (isApprovalStatusError) {
      t8 = "Unavailable";
      break bb0;
    }
    if (approvedCount === null) {
      t8 = "Checking";
      break bb0;
    }
    if (typeof maxWinners === "number" && Number.isFinite(maxWinners)) {
      let t9;
      if ($[12] !== approvedCount) {
        t9 = formatNumberWithCommas(approvedCount);
        $[12] = approvedCount;
        $[13] = t9;
      } else {
        t9 = $[13];
      }
      const approved = t9;
      let t10;
      if ($[14] !== maxWinners) {
        t10 = formatNumberWithCommas(maxWinners);
        $[14] = maxWinners;
        $[15] = t10;
      } else {
        t10 = $[15];
      }
      const max = t10;
      t8 = `${approved} / ${max}`;
      break bb0;
    }
    let t9;
    if ($[16] !== approvedCount) {
      t9 = formatNumberWithCommas(approvedCount);
      $[16] = approvedCount;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t8 = t9;
  }
  const approvedLabel = t8;
  let statusLabel;
  if (closeStatus === "max_reached") {
    statusLabel = "Max approvals reached";
  } else {
    if (closeStatus === "ended") {
      statusLabel = "Approval window ended";
    } else {
      if (isApprovalStatusError) {
        statusLabel = "Unable to check approvals";
      } else {
        if (approvedCount === null) {
          statusLabel = "Checking";
        } else {
          if (endTime !== null) {
            let t9;
            if ($[18] !== currentMillis || $[19] !== endTime) {
              t9 = formatTimeLeft(endTime, currentMillis);
              $[18] = currentMillis;
              $[19] = endTime;
              $[20] = t9;
            } else {
              t9 = $[20];
            }
            statusLabel = t9;
          } else {
            statusLabel = "Open";
          }
        }
      }
    }
  }
  let statusTextClassName = "tw-text-amber-200";
  if (isApprovalStatusError) {
    statusTextClassName = "tw-text-rose-200";
  } else {
    if (closeStatus === null) {
      statusTextClassName = "tw-text-iron-100";
    }
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="tw-mb-1 tw-text-xs tw-font-medium tw-text-iron-500">Threshold</p>;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== thresholdLabel) {
    t10 = <div>{t9}<p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-iron-100">{thresholdLabel}</p></div>;
    $[22] = thresholdLabel;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <p className="tw-mb-1 tw-text-xs tw-font-medium tw-text-iron-500">Approved</p>;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== approvedLabel) {
    t12 = <div>{t11}<p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-iron-100">{approvedLabel}</p></div>;
    $[25] = approvedLabel;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="tw-mb-1 tw-text-xs tw-font-medium tw-text-iron-500">Status</p>;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = `tw-mb-0 tw-text-sm tw-font-semibold ${statusTextClassName}`;
  let t15;
  if ($[28] !== statusLabel || $[29] !== t14) {
    t15 = <div>{t13}<p className={t14}>{statusLabel}</p></div>;
    $[28] = statusLabel;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t10 || $[32] !== t12 || $[33] !== t15) {
    t16 = <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-3">{t10}{t12}{t15}</div>;
    $[31] = t10;
    $[32] = t12;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== isApprovalStatusError || $[36] !== retryApprovalStatus) {
    t17 = isApprovalStatusError && <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"><p className="tw-mb-0 tw-text-xs tw-font-medium tw-text-iron-400">Unable to check approval status. Voting and create controls are paused until the check succeeds.</p>{retryApprovalStatus && <button type="button" onClick={retryApprovalStatus} className="tw-inline-flex tw-w-fit tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-600 tw-bg-iron-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-iron-100 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-iron-300 desktop-hover:hover:tw-border-iron-400 desktop-hover:hover:tw-bg-iron-800">Try again</button>}</div>;
    $[35] = isApprovalStatusError;
    $[36] = retryApprovalStatus;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t16 || $[39] !== t17) {
    t18 = <div className="tw-mt-2 tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-px-3 tw-py-3 md:tw-mt-4">{t16}{t17}</div>;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
}
function _temp(tick) {
  return tick + 1;
}